/**
 * Sync runtime context into the in-house error reporter.
 *
 * The reporter holds a mutable `dynamicContext` bag that gets attached
 * to every report. This component watches the auth + active-context
 * state and keeps that bag in sync whenever the operator logs in,
 * switches scope, or navigates. Mount once inside the providers that
 * give us scope + identity (see App.tsx mount site).
 *
 * Renders nothing.
 */

import { useEffect } from 'react';

import { useAuth } from '@/core/auth/useAuth';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { setReporterContext } from './errorReporter';

export function ReporterContextSync() {
  const { user } = useAuth();
  const { activeContext } = useActiveContext();

  useEffect(() => {
    setReporterContext({
      // `companyId` is the tenant schema name (see authService.ts:216
      // where me.company_schema → companyId during /me/ mapping).
      tenantSchema: user?.companyId ?? '',
      userId: user?.id ?? '',
      // Email is PII — the backend scrubs it again at ingest, but
      // keeping it in the payload helps the AI brief render a human
      // name for the impacted user.
      userEmail: user?.email ?? '',
      activeContext: activeContext
        ? `${activeContext.type}:${activeContext.id}`
        : '',
      release: (import.meta.env.VITE_APP_VERSION as string | undefined)
        ?? (import.meta.env.MODE as string | undefined)
        ?? '',
    });
  }, [user, activeContext]);

  return null;
}
